import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError } from './client';
import { createApiToken, createOidcPolicy, deleteOidcPolicy, listApiTokens, listOidcPolicies, revokeApiToken, updateOidcPolicy, type ApiTokenCreateInput, type OidcPolicyCreateInput, type OidcPolicyPatchInput } from './endpoints';
import { ApiToken, type OidcPolicy } from './schemas';
import { qk } from './queryKeys';

/* ---------- Access: API tokens and CI trust (OIDC) ---------- */

/** Every page of a cursor list (bounded: 10 pages of 200). */
async function allPages<T>(load: (cursor?: string) => Promise<{ data: T[]; nextCursor: string | null }>): Promise<T[]> {
  const out: T[] = [];
  let cursor: string | undefined;
  for (let i = 0; i < 10; i += 1) {
    const page = await load(cursor);
    out.push(...page.data);
    if (!page.nextCursor) break;
    cursor = page.nextCursor;
  }
  return out;
}

/**
 * My personal tokens (admins also see the org's service tokens). Switching `includeRevoked` keeps the previous list
 * on screen (`isPlaceholderData`) until the other one arrives.
 */
export const useApiTokens = (opts: { includeRevoked?: boolean; enabled?: boolean } = {}) =>
  useQuery({
    queryKey: qk.apiTokens(opts.includeRevoked ?? false),
    queryFn: () => allPages((cursor) => listApiTokens({ includeRevoked: opts.includeRevoked ?? false }, cursor)),
    enabled: opts.enabled ?? true,
    placeholderData: (previous: ApiToken[] | undefined) => previous,
  });

export function useApiTokenMutations() {
  const qc = useQueryClient();
  const settle = () => void qc.invalidateQueries({ queryKey: ['api-tokens'] });
  const create = useMutation({
    mutationFn: (body: ApiTokenCreateInput) => createApiToken(body),
    // The response holds the plaintext: the mutation cache must not keep it once the dialog lets go of it.
    gcTime: 0,
    onSuccess: (created) => {
      const row = ApiToken.parse(created); // zod drops `token`: only the metadata reaches the list caches
      for (const all of [false, true]) qc.setQueryData<ApiToken[]>(qk.apiTokens(all), (old) => (old ? [row, ...old.filter((t) => t.id !== row.id)] : old));
      settle();
    },
  });
  const revoke = useMutation({
    mutationFn: (tokenId: string) => revokeApiToken(tokenId),
    onSuccess: (_none, tokenId) => {
      const at = new Date().toISOString();
      qc.setQueryData<ApiToken[]>(qk.apiTokens(true), (old) => old?.map((t) => (t.id === tokenId ? { ...t, revokedAt: t.revokedAt ?? at } : t)));
      qc.setQueryData<ApiToken[]>(qk.apiTokens(false), (old) => old?.filter((t) => t.id !== tokenId));
      settle();
    },
  });
  return { create, revoke };
}

/** Every trust policy of the org (admin only: pass `enabled: false` for other roles, the API answers 403). */
export const useOidcPolicies = (enabled = true) => useQuery({ queryKey: qk.oidcPolicies, queryFn: () => allPages((cursor) => listOidcPolicies(cursor)), enabled });

export function useOidcPolicyMutations() {
  const qc = useQueryClient();
  const put = (p: OidcPolicy) => qc.setQueryData<OidcPolicy[]>(qk.oidcPolicies, (old) => (old ? (old.some((x) => x.id === p.id) ? old.map((x) => (x.id === p.id ? p : x)) : [...old, p]) : old));
  const settle = () => void qc.invalidateQueries({ queryKey: qk.oidcPolicies });
  const create = useMutation({
    mutationFn: (v: { body: OidcPolicyCreateInput; idempotencyKey: string }) => createOidcPolicy(v.body, v.idempotencyKey),
    onSuccess: (p) => {
      put(p);
      settle();
    },
  });
  const update = useMutation({
    mutationFn: (v: { policyId: string; body: OidcPolicyPatchInput; etag?: string }) => updateOidcPolicy(v.policyId, v.body, v.etag),
    onSuccess: (p) => {
      put(p);
      settle();
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 412) settle();
    },
  });
  const remove = useMutation({
    mutationFn: (policyId: string) => deleteOidcPolicy(policyId),
    onSuccess: (_none, policyId) => {
      qc.setQueryData<OidcPolicy[]>(qk.oidcPolicies, (old) => old?.filter((x) => x.id !== policyId));
      settle();
    },
  });
  return { create, update, remove };
}
